import { useCallback, useRef } from 'react'
import { Trans, useLingui } from '@lingui/react/macro'
import { getFragment, GraphQL, SharedAPI } from '@guestgoodie/graphql'
import { Add01Icon, Delete01Icon } from '@hugeicons/core-solid-rounded'

import * as Form from '@/features/form'
import { graphqlToForm } from '@/features/translations'
import { useBranding } from '@/features/branding'
import Input from '@/components/ui/Input'
import Button from '@/components/ui/Button'
import ConfirmDialog from '@/components/ui/ConfirmDialog'
import { Suspense } from '@/components/ui/Suspense'
import { FormData, useBanner } from '../hooks/useBanner'
import { useBanners } from '../hooks/useBanners'

function BannerForm({ banner }: { banner?: GraphQL.BannerFragmentFragment }) {
  const { t } = useLingui()
  const ref = useRef<Form.RefType<FormData>>(null)

  const {
    branding: { supportedLocales }
  } = useBranding()

  const name = banner?.name

  const {
    create,
    update,
    remove,
    createLoading,
    updateLoading,
    deleteLoading
  } = useBanner(banner?.id)

  const reset = useCallback(() => {
    ref.current?.reset({ name: '', translations: {} })
  }, [])

  return (
    <Form.Root<FormData>
      ref={ref}
      overrideClassName="flex flex-row gap-1"
      onSubmit={banner ? update : create}
      onSubmittedSuccessfully={banner ? () => null : reset}
      defaultValues={{
        name: banner?.name ?? '',
        translations: graphqlToForm(
          (banner?.translations ?? []).flatMap(trans =>
            getFragment(SharedAPI.translation.TRANSLATION_FRAGMENT, trans)
          ),
          ['name'],
          supportedLocales ?? []
        )
      }}
    >
      <div className="flex flex-row gap-2 items-center flex-1 px-2">
        <Form.Field
          name="name"
          rules={{
            maxLength: {
              value: 255,
              message: t`Can't be longer than 255 characters.`
            },
            validate: {
              required: Form.Validations.requiredWithTrimValidation(
                t`This field is required`
              )
            }
          }}
        >
          {({ renderProps: { onChange, ...props } }) => (
            <Input
              onChange={event => onChange(event.target.value)}
              type="text"
              {...props}
            />
          )}
        </Form.Field>
        {!banner && (
          <Form.Submit
            loading={createLoading}
            onlyValidWhenDirty
            Icon={Add01Icon}
          >
            <Trans>Add</Trans>
          </Form.Submit>
        )}
        {banner && (
          <>
            <Form.Submit loading={updateLoading} onlyValidWhenDirty>
              <Trans>Save</Trans>
            </Form.Submit>
            <ConfirmDialog
              title={t`Delete Banner`}
              message={t`Are you sure you want to delete the banner with the name "${name}", this cannot be reversed?`}
              onConfirm={remove}
              asChild
            >
              <Button
                variant="outline"
                type="button"
                aria-label={t`Delete`}
                Icon={Delete01Icon}
                loading={deleteLoading}
              />
            </ConfirmDialog>
          </>
        )}
      </div>
    </Form.Root>
  )
}

function BannersForm() {
  const banners = useBanners()

  return (
    <div className="flex flex-col gap-2.5">
      <BannerForm />
      <div className="flex flex-col gap-2.5 overflow-y-auto">
        <Form.Translation translatableFields={['name']}>
          {banners?.map(function (banner) {
            return <BannerForm banner={banner} key={banner.id} />
          })}
        </Form.Translation>
      </div>
    </div>
  )
}

export function BannersFormSuspended() {
  return (
    <Suspense>
      <BannersForm />
    </Suspense>
  )
}
